/// <reference types="@types/cloudflare-turnstile" />
import { createEffect, createSignal, onMount } from 'solid-js';
import { createStore } from 'solid-js/store';
import type { SetStoreFunction } from 'solid-js/store';

export function TurnstileScript() {
	return <script src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit" async defer />;
}

type TurnstileData = {
	loaded: boolean;
	rendered: boolean;
	waiting: boolean;
	error?: string | undefined;
	response?: string | undefined;
	ref?: HTMLElement | undefined;
};

type Props = {
	siteKey: string;
	name?: string;
	onError: (error: string, tries: number) => void;
	onSubmit: () => void;
};

export function createTurnstile(props: Props): [TurnstileData, SetStoreFunction<TurnstileData>] {
	const [turnstile, setTurnstile] = createStore<TurnstileData>({ loaded: false, rendered: false, waiting: false });
	const [tries, setTries] = createSignal(0);

	function initTurnstile() {
		if (turnstile.ref) {
			if (typeof window.turnstile !== 'undefined' && !turnstile.rendered) {
				setTurnstile('rendered', true);
				window.turnstile.render(turnstile.ref!, {
					action: 'user-exists',
					sitekey: props.siteKey,
					size: 'flexible',
					theme: 'light',
					'response-field-name': props.name,
					'error-callback': (error: string) => {
						const numTries = tries() + 1;
						setTries(numTries);
						if ((/^(3|6)00\d{3}$/.test(error) || /^1106(0|2)\d$/.test(error)) && numTries < 3) {
							window.turnstile.reset(turnstile.ref);
							return;
						}

						props.onError(error, numTries);
						setTurnstile({
							response: error,
							error: /^\d+$/.test(error)
								? 'We were unable to verify your browser. Please refresh the page and try again.'
								: error,
						});
					},
					callback: function (token: string) {
						setTurnstile('response', token);
					},
				});
			} else if (!turnstile.rendered) {
				// fallback for test and in case turnstile scripts don't load
				const el = document.createElement('input');
				el.type = 'hidden';
				el.setAttribute('name', props.name ?? 'cf-turnstile-response');
				el.setAttribute('value', '');
				turnstile.ref.appendChild(el);
			}
		}
	}

	createEffect(() => {
		if (turnstile.waiting && turnstile.response) {
			props.onSubmit();
		}
	});

	createEffect(() => {
		if (turnstile.loaded && !turnstile.rendered && turnstile.ref) {
			initTurnstile();
		}
	});

	onMount(() => {
		initTurnstile();
	});

	return [turnstile, setTurnstile];
}

declare global {
	interface Window {
		turnstile: Turnstile.Turnstile;
	}
}
